import styled from 'styled-components'

// Room badge color mapping
const ROOM_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: 'rgba(59,130,246,0.12)', text: '#2563EB' },
  2: { bg: 'rgba(16,185,129,0.12)', text: '#059669' },
  3: { bg: 'rgba(245,158,11,0.12)', text: '#D97706' },
  4: { bg: 'rgba(239,68,68,0.12)', text: '#DC2626' },
}

const DEFAULT_ROOM_COLOR = { bg: 'rgba(139,92,246,0.12)', text: '#7C3AED' }

export const getRoomBadgeColor = (roomNb: number) => ROOM_COLORS[roomNb] || DEFAULT_ROOM_COLOR

export const FloorHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.large};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.background.tertiary};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
  }
`

export const FloorHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
`

export const FloorNumber = styled.div<{ $type: string }>`
  min-width: 18px;
  height: 18px;
  padding: 0 3px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};

  ${({ $type }) => {
    switch ($type) {
      case 'commercial':
        return `
          background: rgba(255, 149, 0, 0.15);
          color: #CC7700;
        `
      default:
        return `
          background: rgba(255, 204, 0, 0.1);
          color: #CC9900;
        `
    }
  }}
`

export const FloorName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FloorMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`

export const UnitTable = styled.div`
  width: 100%;
  min-width: 560px;
`

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 64px 1fr 1fr;
  column-gap: ${({ theme }) => theme.spacing.huge};
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80px 64px 1fr 1fr;
  column-gap: ${({ theme }) => theme.spacing.huge};
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: background ${({ theme }) => theme.transitions.fast};
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const TableCell = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  overflow: hidden;
  min-width: 0;

  > * {
    width: 100%;
  }
`

export const RoomBadge = styled.span<{ $roomNb: number }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ $roomNb }) => getRoomBadgeColor($roomNb).bg};
  color: ${({ $roomNb }) => getRoomBadgeColor($roomNb).text};
`

export const PriceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 0;
  padding-right: ${({ theme }) => theme.spacing.xlarge};
`

export const PriceValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`

export const PriceUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ConfidenceBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  overflow: visible;
  position: relative;
`

export const ConfidenceFill = styled.div<{ $color: 'rent' | 'purchase' }>`
  position: absolute;
  left: 20%;
  width: 40%;
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ $color, theme }) =>
    $color === 'purchase' ? theme.colors.success : theme.colors.primary};
`

export const ConfidenceDot = styled.div<{ $color: 'rent' | 'purchase' }>`
  position: absolute;
  left: 40%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1.5px solid
    ${({ $color, theme }) => ($color === 'purchase' ? theme.colors.success : theme.colors.primary)};
`

export const PriceRange = styled.div`
  font-size: 7px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  white-space: nowrap;
`

export const ExpandButton = styled.button<{ $expanded?: boolean }>`
  width: 23px;
  height: 23px;
  background: ${({ $expanded }) => ($expanded ? 'rgba(2, 148, 223, 0.08)' : 'rgba(0, 0, 0, 0.03)')};
  border: 1px solid
    ${({ $expanded, theme }) => ($expanded ? theme.colors.primary : theme.colors.border.default)};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $expanded, theme }) =>
    $expanded ? theme.colors.primary : theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  padding: 0;

  &:hover {
    background: rgba(2, 148, 223, 0.08);
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const ExpandedRow = styled.div`
  background: rgba(2, 148, 223, 0.03);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: ${({ theme }) => theme.spacing.default} ${({ theme }) => theme.spacing.large};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.large};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default};
    gap: ${({ theme }) => theme.spacing.default};
    flex-wrap: wrap;
  }
`

export const ExpandedAccent = styled.div`
  width: 3px;
  min-height: 38px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`

export const ExpandedFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.large};
  flex: 1;

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.default};
  }
`

export const ExpandedField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const ExpandedLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.secondary};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`

export const ExpandedValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const NoPriceText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const EmptyFloorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};

  p {
    margin-bottom: ${({ theme }) => theme.spacing.default};
  }
`
